<template>
    <div class="tw-bg-white tw-rounded-lg tw-mt-4 tw-mb-8">
        <v-form
            class="tw-p-6"
            ref="form"
            @submit.prevent="submitChangePassword">
            <p class="tw-font-bold tw-text-secondary tw-text-left tw-text-xl tw-mb-4">{{ $t('reservedArea.profile.changePassword.title') }}</p>
            <v-row>
                <v-col
                    cols="12"
                    md="6">
                    <p class="tw-font-semibold tw-text-secondary tw-text-left tw-text-base tw-mb-3">{{ $t('reservedArea.profile.changePassword.currentPassword') }}</p>
                    <v-text-field
                        v-model="currentPassword"
                        name="currentPassword"
                        autocomplete="current-password"
                        :label="$t('reservedArea.profile.changePassword.currentPassword')"
                        :rules="[(v) => rules.required(v, $t('reservedArea.profile.changePassword.requiredField'))]"
                        :type="isCurrentPasswordVisible ? 'text' : 'password'"
                        :append-inner-icon="isCurrentPasswordVisible ? 'mdi-eye-off' : 'mdi-eye'"
                        @click:append-inner="isCurrentPasswordVisible = !isCurrentPasswordVisible"
                        variant="outlined"
                        hide-details="auto"></v-text-field>
                </v-col>
            </v-row>
            <v-row>
                <v-col
                    cols="12"
                    md="6">
                    <p class="tw-font-semibold tw-text-secondary tw-text-left tw-text-base tw-mb-3">{{ $t('reservedArea.profile.changePassword.newPassword') }}</p>
                    <v-text-field
                        v-model="password"
                        name="password"
                        autocomplete="new-password"
                        :label="$t('reservedArea.profile.changePassword.newPassword')"
                        :rules="[
                            (v) => rules.required(v, $t('reservedArea.profile.changePassword.requiredField')),
                            (v) => rules.min(v, 6, $t('reservedArea.profile.changePassword.invalidPasswordLength')),
                            (v) => v !== currentPassword || $t('reservedArea.profile.changePassword.passwordMustBeDifferent'),
                        ]"
                        :type="isPasswordVisible ? 'text' : 'password'"
                        :append-inner-icon="isPasswordVisible ? 'mdi-eye-off' : 'mdi-eye'"
                        @click:append-inner="isPasswordVisible = !isPasswordVisible"
                        variant="outlined"
                        hide-details="auto"></v-text-field>
                </v-col>
                <v-col
                    cols="12"
                    md="6">
                    <p class="tw-font-semibold tw-text-secondary tw-text-left tw-text-base tw-mb-3">{{ $t('reservedArea.profile.changePassword.confirmPassword') }}</p>
                    <v-text-field
                        v-model="passwordConfirmation"
                        name="passwordConfirmation"
                        autocomplete="new-password"
                        :label="$t('reservedArea.profile.changePassword.confirmPassword')"
                        :rules="[
                            (v) => rules.required(v, $t('reservedArea.profile.changePassword.requiredField')),
                            (v) => rules.min(v, 6, $t('reservedArea.profile.changePassword.invalidPasswordLength')),
                            (v) => rules.comparePassword(v, password, $t('reservedArea.profile.changePassword.passwordsDontMatch')),
                        ]"
                        :type="isPasswordConfirmationVisible ? 'text' : 'password'"
                        :append-inner-icon="isPasswordConfirmationVisible ? 'mdi-eye-off' : 'mdi-eye'"
                        @click:append-inner="isPasswordConfirmationVisible = !isPasswordConfirmationVisible"
                        variant="outlined"
                        hide-details="auto"></v-text-field>
                </v-col>
            </v-row>
            <div
                class="tw-flex tw-justify-end tw-z-10 tw-mt-6 tw-mb-4"
                v-show="alertMessage">
                <v-alert
                    class="!tw-justify-start tw-w-full"
                    dense
                    variant="tonal"
                    :type="alertType"
                    >{{ alertMessage }}</v-alert
                >
            </div>
            <div class="tw-flex tw-justify-center lg:tw-justify-end tw-mt-6">
                <v-btn
                    variant="flat"
                    type="submit"
                    class="font-weight-bold !tw-text-base !tw-py-1.5"
                    color="secondary"
                    max-height="none"
                    height="auto"
                    rounded="xs"
                    size="x-large"
                    :loading="isLoading"
                    @click.prevent="submitChangePassword()">
                    {{ $t('reservedArea.profile.changePassword.submit') }}
                </v-btn>
            </div>
        </v-form>
    </div>
</template>

<script setup lang="ts">
    import { ref } from 'vue';
    import { VForm } from 'vuetify/lib/components/index.mjs';
    import { changePassword } from '@/api/auth';
    import { useAuthStore } from '@/store/modules/auth';
    import { useI18n } from 'vue-i18n';
    import rules from '@/composables/rules';
    import slugify from 'slugify';
    import _ from 'lodash';

    const { t: $t } = useI18n();
    const authStore = useAuthStore();

    const form = ref(null) as any as VForm;

    const currentPassword = ref('');
    const password = ref('');
    const passwordConfirmation = ref('');

    const isCurrentPasswordVisible = ref(false);
    const isPasswordVisible = ref(false);
    const isPasswordConfirmationVisible = ref(false);

    const isLoading = ref(false);
    const alertMessage = ref('');
    const alertType = ref<'success' | 'error'>('success');

    const resetForm = () => {
        currentPassword.value = '';
        password.value = '';
        passwordConfirmation.value = '';
        form.value?.resetValidation();
    };

    const submitChangePassword = async () => {
        if (!form.value) return;

        alertMessage.value = '';
        form.value.resetValidation();

        const { valid } = await form.value.validate();
        if (!valid) return;

        isLoading.value = true;

        try {
            const { data } = await changePassword(
                {
                    currentPassword: currentPassword.value,
                    password: password.value,
                    passwordConfirmation: passwordConfirmation.value,
                },
                {
                    headers: {
                        Authorization: `Bearer ${authStore.getToken}`,
                    },
                },
            );

            if (data?.jwt) {
                authStore.setToken(data.jwt);
            }

            resetForm();
            alertMessage.value = $t('reservedArea.profile.changePassword.successText');
            alertType.value = 'success';
        } catch (error) {
            const messageKey = slugify(_.get(error, 'response.data.error.message', 'unknown_error'), {
                replacement: '_',
                trim: true,
                lower: true,
            });
            alertMessage.value = $t(`auth.errors.${messageKey}`, '', {
                default: $t('auth.errors.unknown_error'),
            });
            alertType.value = 'error';
        } finally {
            isLoading.value = false;
        }
    };
</script>

<style scoped></style>
<style>
    .v-input--density-default {
        --v-input-padding-top: 0.2rem;
    }
</style>
